<template>
  <div class="edit-general-tab-content">
    <a-spin v-if="loading" id="loading-general-component-data"></a-spin>

    <a-row :gutter="[16,16]">
      <a-col :span="8">
        <a-card title="Geldverstrekkers">
          <a-form-item :has-feedback="hasFeedback('hypotheeknummer')"
                       :validate-status="hasFeedback('hypotheeknummer') ? 'error' : null"
                       :help="getFeedbackString('hypotheeknummer')"
                       label="Hypotheeknummer">
            <a-input v-model="mortgage.hypotheeknummer"
                     @change="deleteError('hypotheeknummer')"
                     id="hypotheeknummer"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('financier_agency_id')"
                       :validate-status="hasFeedback('financier_agency_id') ? 'error' : null"
                       :help="getFeedbackString('financier_agency_id')"
                       label="Geldverstrekker">
            <a-select v-model="mortgage.financier_agency_id"
                      show-search
                      :filter-option="filterOption"
                      @change="(event) => {financierAgencyChanged(event); deleteError('financier_agency_id')}">
              <a-select-option v-for="agency in enums.agencies" :key="agency.id" :value="agency.id">
                {{ agency.hdn_code }} {{ agency.name }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('loan_type')"
                       :validate-status="hasFeedback('loan_type') ? 'error' : null"
                       :help="getFeedbackString('loan_type')"
                       label="Productnaam">
            <a-select v-model="mortgage.loan_type"
                      @change="loanTypeChanged"
                      :loading="loadingFinancierAgency"
                      show-search
                      :filter-option="filterOption"
                      :disabled="!enums.hasOwnProperty('loanTypes') || enums.loanTypes.length === 0">
              <a-select-option v-for="(value, key) in enums.loanTypes" :key="key" :value="key">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card title="Hypotheek">
          <a-form-item :has-feedback="hasFeedback('status')"
                       :validate-status="hasFeedback('status') ? 'error' : null"
                       :help="getFeedbackString('status')"
                       label="Status">
            <a-select v-model="mortgage.status" @change="deleteError('status')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.statuses" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('hypotheciareInschrijving')"
                       :validate-status="hasFeedback('hypotheciareInschrijving') ? 'error' : null"
                       :help="getFeedbackString('hypotheciareInschrijving')"
                       label="Hypothecaire inschrijving">
            <amount-input v-model="mortgage.hypotheciareInschrijving"
                          @change="deleteError('hypotheciareInschrijving')"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('bouwdepot')"
                       :validate-status="hasFeedback('bouwdepot') ? 'error' : null"
                       :help="getFeedbackString('bouwdepot')"
                       label="Bouwdepot">
            <amount-input v-model="mortgage.bouwdepot" @change="deleteError('bouwdepot')"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('rangorde')"
                       :validate-status="hasFeedback('rangorde') ? 'error' : null"
                       :help="getFeedbackString('rangorde')"
                       label="Rangorde">
            <a-select v-model="mortgage.rangorde" @change="deleteError('rangorde')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="value in [1,2,3,4,5,6,7,8,9,10]" :key="value" :value="value">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('productLabelId')"
                       :validate-status="hasFeedback('productLabelId') ? 'error' : null"
                       :help="getFeedbackString('productLabelId')"
                       label="Product labels"
                       v-if="enums.productLabels && enums.productLabels.length > 0">
            <a-select v-model="mortgage.productLabelId" @change="deleteError('productLabelId')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.productLabels" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card title="Hypotheekgevers">
          <a-form-item
              :has-feedback="hasFeedback('is_shared')"
              :validate-status="hasFeedback('is_shared') ? 'error' : null"
              label="Gezamelijk hypotheek"
              help="Is dit een gezamenlijke hypotheek met de partner?">
            <a-switch v-model="mortgage.is_shared" @change="onIsSharedChange()" id="is-shared"/>
          </a-form-item>

          <a-form-item
              :has-feedback="hasFeedback('customer_liability_portion')"
              :validate-status="hasFeedback('customer_liability_portion') ? 'error' : null"
              :help="getFeedbackString('customer_liability_portion')"
              :label="`Deel ${ getCustomer ? getCustomer.first_name : 'hoofdklant' }`">
            <percentage-input
                v-model="mortgage.customer_liability_portion"
                @change="deleteError('customer_liability_portion')"
                @blur="handleCustomerLiabilityPortionBlur"
                id="customer-liability-portion"
                :min="0"
                :max="100"
            />
          </a-form-item>

          <a-form-item :label="`Deel ${ getPartner && mortgage.is_shared ? getPartner.first_name : 'derden' }`"
                       disabled>
            <percentage-input :value="100 - mortgage.customer_liability_portion"
                              :min="0"
                              :max="100"
                              disabled
            />
          </a-form-item>

        </a-card>
      </a-col>
    </a-row>

    <a-row :gutter="[16,16]">
      <a-col :span="8">
        <a-card title="Bankgarantie">
          <a-form-item :has-feedback="hasFeedback('bank_guarantee')"
                       :validate-status="hasFeedback('bank_guarantee') ? 'error' : null"
                       :help="getFeedbackString('bank_guarantee')"
                       label="Bankgarantie aanvragen?">
            <a-switch v-model="mortgage.bank_guarantee" @change="deleteError('bank_guarantee')"></a-switch>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('bank_guarantee_amount')"
                       :validate-status="hasFeedback('bank_guarantee_amount') ? 'error' : null"
                       :help="getFeedbackString('bank_guarantee_amount')"
                       label="Bankgarantie">
            <amount-input v-model="mortgage.bank_guarantee_amount"
                          @change="deleteError('bank_guarantee_amount')"
                          :disabled="!mortgage.bank_guarantee"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('bank_guarantee_costs')"
                       :validate-status="hasFeedback('bank_guarantee_costs') ? 'error' : null"
                       :help="getFeedbackString('bank_guarantee_costs')"
                       label="Bankgarantie kosten">
            <amount-input v-model="mortgage.bank_guarantee_costs"
                          @change="deleteError('bank_guarantee_costs')"
                          :disabled="!mortgage.bank_guarantee"/>
          </a-form-item>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card title="Details">
          <a-form-item :has-feedback="hasFeedback('passeerdatum')"
                       :validate-status="hasFeedback('passeerdatum') ? 'error' : null"
                       :help="getFeedbackString('passeerdatum')"
                       label="Passeerdatum">
            <a-date-picker :format="DateFormatEnum.DISPLAY_DATE" :value-format="DateFormatEnum.DATE"
                           v-model="mortgage.passeerdatum"
                           @change="deleteError('passeerdatum')"
            ></a-date-picker>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('redenHypotheek')"
                       :validate-status="hasFeedback('redenHypotheek') ? 'error' : null"
                       :help="getFeedbackString('redenHypotheek')"
                       label="Reden hypotheek">
            <a-input v-model="mortgage.redenHypotheek" @change="deleteError('redenHypotheek')"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('arrangement_type')"
                       :validate-status="hasFeedback('arrangement_type') ? 'error' : null"
                       :help="getFeedbackString('arrangement_type')"
                       label="Regeling">
            <a-select v-model="mortgage.arrangement_type" @change="deleteError('arrangement_type')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.arrangementTypes" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('mutation_code')"
                       :validate-status="hasFeedback('mutation_code') ? 'error' : null"
                       :help="getFeedbackString('mutation_code')"
                       label="Mutatie">
            <a-select v-model="mortgage.mutation_code" @change="deleteError('mutation_code')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.mutations" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-card>
      </a-col>

      <a-col :span="8">
        <a-card title="Relaties">
          <a-form-item :has-feedback="hasFeedback('notaris_id')"
                       :validate-status="hasFeedback('notaris_id') ? 'error' : null"
                       :help="getFeedbackString('notaris_id')"
                       label="Notaris">
            <a-select v-model="mortgage.notaris_id" @change="deleteError('notaris_id')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.notaries" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('verkopendMakelaar_id')"
                       :validate-status="hasFeedback('verkopendMakelaar_id') ? 'error' : null"
                       :help="getFeedbackString('verkopendMakelaar_id')"
                       label="Verkopend makelaar">
            <a-select v-model="mortgage.verkopendMakelaar_id" @change="deleteError('verkopendMakelaar_id')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.brokers" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('purchasing_broker_id')"
                       :validate-status="hasFeedback('purchasing_broker_id') ? 'error' : null"
                       :help="getFeedbackString('purchasing_broker_id')"
                       label="Aankopend makelaar">
            <a-select v-model="mortgage.purchasing_broker_id" @change="deleteError('purchasing_broker_id')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.brokers" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('taxateur_id')"
                       :validate-status="hasFeedback('taxateur_id') ? 'error' : null"
                       :help="getFeedbackString('taxateur_id')"
                       label="Taxateur">
            <a-select v-model="mortgage.taxateur_id" @change="deleteError('taxateur_id')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.brokers" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('structural_engineer_id')"
                       :validate-status="hasFeedback('structural_engineer_id') ? 'error' : null"
                       :help="getFeedbackString('structural_engineer_id')"
                       label="Bouwkunding ingenieur">
            <a-select v-model="mortgage.structural_engineer_id" @change="deleteError('structural_engineer_id')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.structuralEngineers" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <div style="margin-top:1em;">
            <p>Staat een partij niet in de lijst?</p>
            <a-button target="_blank" type="primary" :href="route('settings.entities.index')">
              Partijen beheren
            </a-button>
          </div>
        </a-card>
      </a-col>
    </a-row>

    <a-row :gutter="[16,16]">
      <a-col :span="16">
        <mortgage-edit-mortgage-properties
            :mortgage-id="mortgageId"
            :customer-id="customerId"
        ></mortgage-edit-mortgage-properties>
      </a-col>

      <a-col :span="8">
        <a-card title="Onderpand">
          <a-form-item :has-feedback="hasFeedback('koopsom')"
                       :validate-status="hasFeedback('koopsom') ? 'error' : null"
                       :help="getFeedbackString('koopsom')"
                       label="Koopsom">
            <amount-input v-model="mortgage.koopsom" @change="deleteError('koopsom')"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('original_market_value')"
                       :validate-status="hasFeedback('original_market_value') ? 'error' : null"
                       :help="getFeedbackString('original_market_value')"
                       label="Oorspronkelijke marktwaarde">
            <amount-input v-model="mortgage.original_market_value" @change="deleteError('original_market_value')"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('marktwaarde')"
                       :validate-status="hasFeedback('marktwaarde') ? 'error' : null"
                       :help="getFeedbackString('marktwaarde')"
                       label="Marktwaarde">
            <amount-input v-model="mortgage.marktwaarde" @change="deleteError('marktwaarde')"/>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('datumTaxatie')"
                       :validate-status="hasFeedback('datumTaxatie') ? 'error' : null"
                       :help="getFeedbackString('datumTaxatie')"
                       label="Datum taxatie">
            <a-date-picker :format="DateFormatEnum.DISPLAY_DATE" :value-format="DateFormatEnum.DATE"
                           v-model="mortgage.datumTaxatie"
                           @change="deleteError('datumTaxatie')"
            ></a-date-picker>
          </a-form-item>

          <a-form-item :has-feedback="hasFeedback('taxation_type')"
                       :validate-status="hasFeedback('taxation_type') ? 'error' : null"
                       :help="getFeedbackString('taxation_type')"
                       label="Taxatiebron">
            <a-select v-model="mortgage.taxation_type" @change="deleteError('taxation_type')"
                      show-search
                      :filter-option="filterOption"
            >
              <a-select-option v-for="(value, key) in enums.taxationTypes" :key="key" :value="Number(key)">
                {{ value }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import store from '~/store';
import mortgageStore from '~/modules/mortgage/store';
import {mapActions, mapGetters} from "vuex";
import mortgageApi from '~/api/mortgage';
import show_backend_feedback from "~/modules/mortgage/mixins/show_backend_feedback";
import {DateFormatEnum} from "~/enums";

export default {
  name: 'MortgageEditGeneralComponent',
  props: {
    mortgageId: {
      type: Number,
      required: false,
      default: null
    },
    customerId: Number,
    active: Boolean,
    isEditing: Boolean
  },
  data() {
    return {
      DateFormatEnum: DateFormatEnum,
      loading: true,
      enums: [],
      loadingFinancierAgency: false,
      properties: [
        'hypotheeknummer',
        'financier_agency_id',
        'loan_type',
        'status',
        'hypotheciareInschrijving',
        'bouwdepot',
        'rangorde',
        'productLabelId',
        'is_shared',
        'customer_liability_portion',
        'bank_guarantee',
        'bank_guarantee_amount',
        'bank_guarantee_costs',
        'passeerdatum',
        'redenHypotheek',
        'arrangement_type',
        'mutation_code',
        'notaris_id',
        'verkopendMakelaar_id',
        'purchasing_broker_id',
        'taxateur_id',
        'structural_engineer_id',
        'koopsom',
        'original_market_value',
        'marktwaarde',
        'datumTaxatie',
        'taxation_type',
        'secondary_customer_id'
      ]
    }
  },
  created() {
    if (!store.hasModule('mortgage')) {
      store.registerModule('mortgage', mortgageStore)
    }
  },
  mixins: [
    show_backend_feedback
  ],
  async mounted() {
    this.loading = true;
    try {
      if (!this.mortgage) {
        await this.fetchMortgage(this.mortgageId)
      }

      await this.fetchListsToCreateOrEdit(this.mortgageId)
      await this.fetchCustomer(this.customerId)

      if (this.getCustomerById(this.customerId) && this.getCustomerById(this.customerId)?.partner_id !== null) {
        await this.fetchCustomer(this.getCustomerById(this.customerId).partner_id);
      }

      if (this.mortgage && this.mortgage.financier_agency_id) {
        await this.fetchNewLoanTypes(this.mortgage.financier_agency_id)
      }
    } catch (e) {
      this.$notification.error({
        message: 'Er is iets fout gegaan met het ophalen van de benodigde informatie',
        description: 'Probeer het opnieuw'
      })
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchListsToCreateOrEdit(mortgageId) {
      this.enums = await mortgageApi.getListsToCreateOrEdit(mortgageId);
    },
    async financierAgencyChanged(agencyId) {
      this.loadingFinancierAgency = true;

      this.mortgage.loan_type = null;
      await this.fetchNewLoanTypes(agencyId)

      this.loadingFinancierAgency = false;
    },
    async fetchNewLoanTypes(agencyId) {
      let hdnCode = this.enums.agencies.find(agency => agency.id === agencyId).hdn_code;

      this.enums.loanTypes = await mortgageApi.getLoanTypes(hdnCode, this.mortgageId)
    },
    loanTypeChanged(loanType) {
      this.$set(this.mortgage, 'loan_type', loanType);
      this.$forceUpdate(); // Important otherwise the UI will not reflect the change

      this.deleteError('loan_type');
    },
    ...mapActions({
      fetchMortgage: "mortgage/fetchMortgage",
      fetchCustomer: "fetchCustomer"
    }),
    onIsSharedChange() {
      this.deleteError('is_shared');
      this.mortgage.secondary_customer_id = this.mortgage.is_shared ? this.getPartner?.id : null;
    },
    handleCustomerLiabilityPortionBlur() {
      if (Number(this.mortgage.customer_liability_portion) > 100) {
        this.mortgage.customer_liability_portion = '100'
      }

      if (Number(this.mortgage.customer_liability_portion) < 0) {
        this.mortgage.customer_liability_portion = '0'
      }
    },
    filterOption(input, option) {
      return (option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0);
    }
  },
  computed: {
    ...mapGetters({
      mortgage: 'mortgage/mortgage',
      getCustomerById: 'getCustomerById'
    }),
    getCustomer() {
      return this.getCustomerById(this.customerId);
    },
    getPartnerId() {
      return this.getCustomer?.partner_id;
    },
    getPartner() {
      return this.getPartnerId !== null ? this.getCustomerById(this.getPartnerId) : null;
    },
    getFeedbackObject() {
      return this.mortgage;
    }
  }
}
</script>

<style scoped>
.ant-select {
  width: 100%;
}
</style>